import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { platformSettingsApi } from '../../services/plotfyApi.js'
import { subscribeRealtimeSync } from '../../lib/realtime.js'
import { getMediaUrl, updateFavicon } from '../../utils/media.js'

// Global in-memory cache to share logo across all BrandMark instances without redundant fetches
let cachedLogoUrl = typeof window !== 'undefined' ? (window.__PLOTFY_LOGO_URL__ ?? null) : null
let hasFetchedSettings = false
const logoSubscribers = new Set()

const DEFAULT_LOGO_ICON = '/images/plotfy-logo-icon.png'
const DEFAULT_LOGO_TEXT = '/images/plotfy-logo-text.png'

function broadcastLogo(url) {
  cachedLogoUrl = url
  if (typeof window !== 'undefined') {
    window.__PLOTFY_LOGO_URL__ = url
    updateFavicon(url)
  }
  logoSubscribers.forEach((callback) => callback(url))
}

export function setGlobalLogoUrl(url) {
  broadcastLogo(url)
}

export function BrandMark({ compact = false, to = '/', className = '' }) {
  const [logoUrl, setLogoUrl] = useState(cachedLogoUrl)
  const [iconError, setIconError] = useState(false)
  const [textError, setTextError] = useState(false)

  useEffect(() => {
    // Subscribe to logo changes
    const handler = (newUrl) => {
      setLogoUrl(newUrl)
      setIconError(false)
    }
    logoSubscribers.add(handler)

    // Initial fetch if not fetched yet
    if (!hasFetchedSettings) {
      hasFetchedSettings = true
      platformSettingsApi
        .getPublicSettings()
        .then((settings) => {
          if (settings?.logoUrl !== undefined) {
            broadcastLogo(settings.logoUrl || null)
          }
        })
        .catch(() => {})
    }

    // Subscribe to realtime multi-tab events
    const unsubscribeSync = subscribeRealtimeSync((event) => {
      if (event?.entity === 'settings' && event.logoUrl !== undefined) {
        broadcastLogo(event.logoUrl || null)
      }
    })

    return () => {
      logoSubscribers.delete(handler)
      unsubscribeSync()
    }
  }, [])

  // Icon Source: custom logo from admin settings or default official ribbon icon
  const iconSrc = logoUrl && !iconError ? getMediaUrl(logoUrl) : DEFAULT_LOGO_ICON

  return (
    <Link
      to={to}
      className={`inline-flex min-h-11 items-center gap-1.5 sm:gap-2 rounded-lg font-bold tracking-[-0.03em] text-copy transition-opacity hover:opacity-95 select-none shrink-0 ${className}`}
      aria-label="Plotfy — beranda"
    >
      {/* 1. Official Logo Icon Mark */}
      <img
        src={iconSrc}
        alt="Plotfy Icon"
        onError={() => setIconError(true)}
        className="size-8 sm:size-9 object-contain shrink-0 drop-shadow-sm bg-transparent"
        loading="eager"
        decoding="async"
      />

      {/* 2. Official Logo Text Wordmark (Hidden on compact mode) */}
      {!compact && (
        !textError ? (
          <img
            src={DEFAULT_LOGO_TEXT}
            alt="Plotfy"
            onError={() => setTextError(true)}
            className="h-6 sm:h-7 w-auto max-w-[125px] sm:max-w-[145px] object-contain shrink-0 drop-shadow-sm ml-0.5"
            loading="eager"
            decoding="async"
          />
        ) : (
          <span className="text-lg sm:text-xl font-bold tracking-[-0.03em] text-copy ml-0.5 whitespace-nowrap">
            Plot<span className="text-brand-600">fy</span>
          </span>
        )
      )}
    </Link>
  )
}
